import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config';
import { playChime } from '../lib/chime';

// Map detected visitor language (English name from the LLM) -> [flag, Greek label]
const LANG_BADGE = {
  greek: ['🇬🇷', 'Ελληνικά'], english: ['🇬🇧', 'Αγγλικά'], russian: ['🇷🇺', 'Ρωσικά'],
  romanian: ['🇷🇴', 'Ρουμανικά'], arabic: ['🇸🇦', 'Αραβικά'], hebrew: ['🇮🇱', 'Εβραϊκά'],
  spanish: ['🇪🇸', 'Ισπανικά'], french: ['🇫🇷', 'Γαλλικά'], mandarin: ['🇨🇳', 'Κινεζικά'],
  chinese: ['🇨🇳', 'Κινεζικά'], hindi: ['🇮🇳', 'Χίντι'], tagalog: ['🇵🇭', 'Ταγκαλόγκ'],
  filipino: ['🇵🇭', 'Ταγκαλόγκ'], serbian: ['🇷🇸', 'Σερβικά'], ukrainian: ['🇺🇦', 'Ουκρανικά'],
  bulgarian: ['🇧🇬', 'Βουλγαρικά'], polish: ['🇵🇱', 'Πολωνικά'], portuguese: ['🇵🇹', 'Πορτογαλικά'],
  italian: ['🇮🇹', 'Ιταλικά'], german: ['🇩🇪', 'Γερμανικά'], turkish: ['🇹🇷', 'Τουρκικά'],
};
const langBadge = (name) => {
  if (!name) return null;
  const k = name.trim().toLowerCase();
  for (const key in LANG_BADGE) { if (k.includes(key)) return LANG_BADGE[key]; }
  return ['🌐', name];
};

/**
 * Admin/agent panel for public visitor live chats.
 * Tabs: active (open) / completed (closed, green).
 * A chat is locked to the agent who claimed it — others are read-only.
 */
export const VisitorChatsPanel = () => {
  const [chats, setChats] = useState([]);
  const [me, setMe] = useState(null);
  const [role, setRole] = useState(null);
  const [tab, setTab] = useState('active'); // 'active' | 'done'
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [lockMsg, setLockMsg] = useState('');
  const [completeModal, setCompleteModal] = useState(false);
  const [collabs, setCollabs] = useState([]);
  const [cForm, setCForm] = useState(null);
  const [cErr, setCErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [onlineStaff, setOnlineStaff] = useState([]);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTarget, setTransferTarget] = useState('');
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const prevTransferCountRef = useRef(0);
  const [qrStats, setQrStats] = useState(null);
  const [quickReplies, setQuickReplies] = useState([]);
  const listRef = useRef(null);
  const prevUnreadRef = useRef(null);
  const lastMsgIdRef = useRef(null);
  const fileBase = API.replace(/\/api$/, '');
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const mediaRecRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recTimerRef = useRef(null);
  const recCancelRef = useRef(false);

  const loadQrStats = useCallback(() => {
    axios.get(`${API}/admin/qr-scan-stats`).then((r) => setQrStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadQrStats();
    axios.get(`${API}/admin/quick-replies`).then((r) => setQuickReplies(r.data.quick_replies || [])).catch(() => {});
  }, [loadQrStats]);

  const resetAnalytics = async () => {
    if (!window.confirm('Καθαρισμός όλων των δοκιμαστικών δεδομένων (QR σαρώσεις & εγκαταστάσεις); Δεν αναιρείται.')) return;
    try {
      await axios.delete(`${API}/admin/analytics/reset`);
      loadQrStats();
    } catch (_e) { /* ignore */ }
  };

  const fetchChats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/visitor-chats`);
      const list = res.data.chats || [];
      setChats(list);
      setMe(res.data.me || null);
      setRole(res.data.role || null);
      setAuthError(false);
      const total = list.reduce((s, c) => s + (c.unread_admin || 0), 0);
      if (prevUnreadRef.current !== null && total > prevUnreadRef.current) playChime();
      prevUnreadRef.current = total;
    } catch (e) {
      console.error('Failed to load chats', e);
      if (e?.response?.status === 401) setAuthError(true);
    }
  }, []);

  const openChat = async (sessionId) => {
    setActiveSessionId(sessionId);
    setLoading(true);
    setLockMsg('');
    try {
      const res = await axios.get(`${API}/admin/visitor-chats/${sessionId}/messages`);
      setActiveChat(res.data.chat);
      if (res.data.me) setMe(res.data.me);
      if (res.data.role) setRole(res.data.role);
      const msgs = res.data.messages || [];
      setMessages(msgs);
      lastMsgIdRef.current = msgs.length ? msgs[msgs.length - 1].id : null;
      setChats((prev) => prev.map((c) => c.session_id === sessionId ? { ...c, unread_admin: 0 } : c));
    } catch (e) {
      console.error('Failed to open chat', e);
    } finally {
      setLoading(false);
    }
  };

  // Whether the current staff member may reply to the active chat
  const canReply = (chat) => {
    if (!chat) return false;
    if (role === 'admin') return true;
    const owner = chat.assigned_agent;
    return !owner || owner === me;
  };
  const isLockedByOther = (chat) => chat && chat.assigned_agent && chat.assigned_agent !== me && role !== 'admin';

  const claimChat = async () => {
    if (!activeSessionId) return;
    setLockMsg('');
    try {
      await axios.post(`${API}/admin/visitor-chats/${activeSessionId}/claim`);
      const res = await axios.get(`${API}/admin/visitor-chats/${activeSessionId}/messages`);
      setActiveChat(res.data.chat);
      fetchChats();
    } catch (e) {
      setLockMsg(e?.response?.data?.detail || 'Δεν ήταν δυνατή η ανάληψη.');
    }
  };

  const completeChat = async () => {
    if (!activeSessionId) return;
    setLockMsg('');
    try {
      await axios.post(`${API}/admin/visitor-chats/${activeSessionId}/complete`);
      const res = await axios.get(`${API}/admin/visitor-chats/${activeSessionId}/messages`);
      setActiveChat(res.data.chat);
      fetchChats();
    } catch (e) {
      setLockMsg(e?.response?.data?.detail || 'Σφάλμα ολοκλήρωσης.');
    }
  };

  const reopenChat = async () => {
    if (!activeSessionId) return;
    try {
      await axios.post(`${API}/admin/visitor-chats/${activeSessionId}/reopen`);
      const res = await axios.get(`${API}/admin/visitor-chats/${activeSessionId}/messages`);
      setActiveChat(res.data.chat);
      fetchChats();
    } catch (e) { /* ignore */ }
  };

  const openCompleteWithCollab = async () => {
    setLockMsg(''); setCErr('');
    try {
      const { data } = await axios.get(`${API}/admin/line-collaborators`);
      setCollabs(data.collaborators || []);
    } catch (_e) { setCollabs([]); }
    const nm = (activeChat?.name || '').trim().split(' ');
    setCForm({
      collaborator_id: '',
      first_name: nm[0] || '',
      last_name: nm.slice(1).join(' ') || '',
      phone: activeChat?.phone || '',
      visit_date: new Date().toISOString().slice(0, 10),
      visit_time: '',
      comments: '',
    });
    setCompleteModal(true);
  };

  const submitCompleteWithCollab = async () => {
    if (!cForm?.collaborator_id) { setCErr('Επιλέξτε συνεργάτη'); return; }
    setSaving(true); setCErr('');
    try {
      const vres = await axios.post(`${API}/admin/line-collaborators/${cForm.collaborator_id}/visits`, {
        first_name: cForm.first_name, last_name: cForm.last_name, phone: cForm.phone,
        visit_date: cForm.visit_date, visit_time: cForm.visit_time, comments: cForm.comments, session_id: activeSessionId,
      });
      const visitId = vres.data.id;
      const col = collabs.find((c) => c.id === cForm.collaborator_id);
      const who = col ? `${col.specialty ? col.specialty + ' — ' : ''}${col.full_name}` : '';
      const place = col ? [col.city, col.address].filter(Boolean).join(', ') : '';
      const when = [cForm.visit_date, cForm.visit_time].filter(Boolean).join(' ');
      const apptMsg = `📅 Κλείσαμε το ραντεβού σας: ${who}${place ? ` (${place})` : ''}${when ? ` — ${when}` : ''}. Παρακαλώ επιβεβαιώστε ότι σας εξυπηρετεί το ραντεβού.`;
      await axios.post(`${API}/admin/visitor-chats/${activeSessionId}/appointment`, { visit_id: visitId, text: apptMsg });
      setCompleteModal(false);
      const res = await axios.get(`${API}/admin/visitor-chats/${activeSessionId}/messages`);
      setMessages(res.data.messages || []);
      setActiveChat(res.data.chat);
      fetchChats();
    } catch (e) {
      setCErr(e?.response?.data?.detail || 'Σφάλμα καταχώρησης ραντεβού');
    } finally { setSaving(false); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !activeSessionId) return;
    const text = reply.trim();
    setReply('');
    setLockMsg('');
    const tmpId = `tmp-${Date.now()}`;
    setMessages((prev) => [...prev, { id: tmpId, sender: 'admin', text, created_at: new Date().toISOString() }]);
    try {
      await axios.post(`${API}/admin/visitor-chats/${activeSessionId}/reply`, { text });
      const res = await axios.get(`${API}/admin/visitor-chats/${activeSessionId}/messages`);
      setMessages(res.data.messages || []);
      setActiveChat(res.data.chat);
      fetchChats();
    } catch (e) {
      setMessages((prev) => prev.filter((m) => m.id !== tmpId));
      if (e?.response?.status === 409) setLockMsg(e?.response?.data?.detail || 'Η συνομιλία έχει αναληφθεί από άλλον αντιπρόσωπο.');
      else console.error('Failed to send reply', e);
    }
  };

  const deleteChat = async (sessionId) => {
    if (!window.confirm('Μετακίνηση αυτής της συνομιλίας στις «Σβησμένες»;')) return;
    try {
      await axios.delete(`${API}/admin/visitor-chats/${sessionId}`);
      setChats((prev) => prev.map((c) => c.session_id === sessionId ? { ...c, deleted: true } : c));
      if (activeSessionId === sessionId) { setActiveSessionId(null); setActiveChat(null); setMessages([]); }
    } catch (e) { console.error('Delete failed', e); }
  };

  const permanentDeleteChat = async (sessionId) => {
    if (!window.confirm('ΜΟΝΙΜΗ διαγραφή αυτής της συνομιλίας; Η ενέργεια δεν αναιρείται.')) return;
    try {
      await axios.delete(`${API}/admin/visitor-chats/${sessionId}/permanent`);
      setChats((prev) => prev.filter((c) => c.session_id !== sessionId));
      if (activeSessionId === sessionId) { setActiveSessionId(null); setActiveChat(null); setMessages([]); }
    } catch (e) { console.error('Permanent delete failed', e); }
  };

  useEffect(() => {
    fetchChats();
    const interval = setInterval(fetchChats, 5000);
    return () => clearInterval(interval);
  }, [fetchChats]);

  // Presence heartbeat + incoming transfer requests
  useEffect(() => {
    const tick = async () => {
      try { await axios.post(`${API}/admin/presence/ping`); } catch (_e) {}
      try { const r = await axios.get(`${API}/admin/transfers/pending`); setPendingTransfers(r.data.transfers || []); } catch (_e) {}
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => clearInterval(id);
  }, []);

  const openTransfer = async () => {
    setLockMsg('');
    try { const r = await axios.get(`${API}/admin/presence/online`); setOnlineStaff(r.data.online || []); } catch (_e) { setOnlineStaff([]); }
    setTransferTarget('');
    setTransferOpen(true);
  };
  const submitTransfer = async () => {
    if (!transferTarget || !activeSessionId) return;
    try {
      await axios.post(`${API}/admin/visitor-chats/${activeSessionId}/transfer`, { to_username: transferTarget });
      setTransferOpen(false);
      fetchChats();
    } catch (e) { setLockMsg(e?.response?.data?.detail || 'Σφάλμα μεταβίβασης'); }
  };
  const acceptTransfer = async (sid) => {
    try {
      await axios.post(`${API}/admin/visitor-chats/${sid}/transfer/accept`);
      setPendingTransfers((p) => p.filter((t) => t.session_id !== sid));
      fetchChats();
      openChat(sid);
    } catch (_e) {}
  };
  const declineTransfer = async (sid) => {
    try {
      await axios.post(`${API}/admin/visitor-chats/${sid}/transfer/decline`);
      setPendingTransfers((p) => p.filter((t) => t.session_id !== sid));
    } catch (_e) {}
  };
  const onReplyChange = (e) => {
    setReply(e.target.value);
    if (activeSessionId) axios.post(`${API}/admin/visitor-chats/${activeSessionId}/typing`, { typing: true }).catch(() => {});
  };

  const sendQuick = async (text) => {
    if (!activeSessionId || !canReply(activeChat)) return;
    const tmpId = `tmp-${Date.now()}`;
    setMessages((prev) => [...prev, { id: tmpId, sender: 'admin', text, created_at: new Date().toISOString() }]);
    setLockMsg('');
    try {
      await axios.post(`${API}/admin/visitor-chats/${activeSessionId}/reply`, { text });
      const res = await axios.get(`${API}/admin/visitor-chats/${activeSessionId}/messages`);
      setMessages(res.data.messages || []);
      setActiveChat(res.data.chat);
      fetchChats();
    } catch (e) {
      setMessages((prev) => prev.filter((m) => m.id !== tmpId));
      if (e?.response?.status === 409) setLockMsg(e?.response?.data?.detail || 'Η συνομιλία έχει αναληφθεί από άλλον αντιπρόσωπο.');
    }
  };

  const startRecording = async () => {
    if (!activeSessionId || !canReply(activeChat) || recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      audioChunksRef.current = [];
      recCancelRef.current = false;
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        clearInterval(recTimerRef.current);
        setRecording(false);
        setRecSecs(0);
        if (recCancelRef.current) return;
        const blob = new Blob(audioChunksRef.current, { type: mr.mimeType || 'audio/webm' });
        if (blob.size < 800) return;
        const ext = (mr.mimeType || '').includes('mp4') ? 'm4a' : (mr.mimeType || '').includes('ogg') ? 'ogg' : 'webm';
        const fd = new FormData();
        fd.append('file', blob, `voice.${ext}`);
        try {
          await axios.post(`${API}/admin/visitor-chats/${activeSessionId}/voice`, fd);
          const res = await axios.get(`${API}/admin/visitor-chats/${activeSessionId}/messages`);
          setMessages(res.data.messages || []);
          setActiveChat(res.data.chat);
          fetchChats();
        } catch (e) {
          if (e?.response?.status === 409) setLockMsg(e?.response?.data?.detail || 'Η συνομιλία έχει αναληφθεί από άλλον.');
        }
      };
      mr.start();
      mediaRecRef.current = mr;
      setRecording(true);
      setRecSecs(0);
      recTimerRef.current = setInterval(() => setRecSecs((s) => {
        if (s >= 120) { try { mr.stop(); } catch (_e) {} }
        return s + 1;
      }), 1000);
    } catch (_e) {
      setLockMsg('Δεν έχουμε πρόσβαση στο μικρόφωνο.');
    }
  };
  const stopRecording = (cancel = false) => {
    recCancelRef.current = cancel;
    try { mediaRecRef.current && mediaRecRef.current.stop(); } catch (_e) {}
  };

  // Sound + tab-title alert when a transfer request arrives (even on another tab)
  useEffect(() => {
    const n = pendingTransfers.length;
    if (n > prevTransferCountRef.current) {
      try { playChime(); } catch (_e) {}
    }
    prevTransferCountRef.current = n;
    document.title = n > 0 ? `(${n}) 🔁 Αίτημα μεταβίβασης — J.HOUSEOFSECRETARY` : 'J.HOUSEOFSECRETARY';
  }, [pendingTransfers]);

  useEffect(() => {
    if (!activeSessionId) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/admin/visitor-chats/${activeSessionId}/messages`);
        const msgs = res.data.messages || [];
        setMessages(msgs);
        setActiveChat(res.data.chat);
        const last = msgs.length ? msgs[msgs.length - 1] : null;
        if (last && last.id !== lastMsgIdRef.current) {
          if (last.sender === 'visitor') playChime();
          lastMsgIdRef.current = last.id;
        }
      } catch (e) { /* ignore */ }
    }, 4000);
    return () => clearInterval(interval);
  }, [activeSessionId]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const formatTime = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString('el-GR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  const isDeleted = (c) => c.deleted === true;
  const isClosed = (c) => c.status === 'closed' && !isDeleted(c);
  const visibleChats = chats.filter((c) =>
    tab === 'deleted' ? isDeleted(c) : tab === 'done' ? isClosed(c) : (!isClosed(c) && !isDeleted(c)));
  const activeCount = chats.filter((c) => !isClosed(c) && !isDeleted(c)).length;
  const doneCount = chats.filter((c) => isClosed(c)).length;
  const deletedCount = chats.filter((c) => isDeleted(c)).length;
  const totalUnread = chats.filter((c) => !isClosed(c) && !isDeleted(c)).reduce((sum, c) => sum + (c.unread_admin || 0), 0);

  const lockedActive = isLockedByOther(activeChat);
  const activeClosed = activeChat && isClosed(activeChat);
  const activeDeleted = activeChat && isDeleted(activeChat);

  return (
    <div className="bg-navy-100/50 rounded-xl border border-slate-700/50 overflow-hidden" data-testid="visitor-chats-panel">
      {pendingTransfers.length > 0 && (
        <div className="p-3 bg-red-900/40 border-b-2 border-red-500 space-y-2 animate-pulse" data-testid="transfer-notifications">
          {pendingTransfers.map((t) => (
            <div key={t.session_id} className="flex items-center justify-between gap-2 flex-wrap" data-testid={`transfer-notif-${t.session_id}`}>
              <span className="text-red-100 text-sm flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-red-500 flex-shrink-0" />🔁 <b>{t.from_name}</b> θέλει να σας μεταβιβάσει τη συνομιλία με <b>{t.visitor_name}</b></span>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => acceptTransfer(t.session_id)} className="text-xs text-white font-semibold rounded px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 transition" data-testid={`transfer-accept-${t.session_id}`}>✓ Αποδοχή</button>
                <button type="button" onClick={() => declineTransfer(t.session_id)} className="text-xs text-red-300 rounded px-3 py-1.5 border border-red-400/50 hover:bg-red-900/30 transition" data-testid={`transfer-decline-${t.session_id}`}>✕ Απόρριψη</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {qrStats && (
        <div className="px-4 py-2 bg-navy-200/40 border-b border-slate-700/60 text-xs text-slate-300 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="qr-scan-stats">
          <span className="text-gold font-semibold">📈 QR σαρώσεις:</span>
          <span>Σύνολο <b className="text-white">{qrStats.total}</b></span>
          <span>Σήμερα <b className="text-white">{qrStats.today}</b></span>
          <span className="text-slate-500">•</span>
          <span className="text-emerald-300 font-semibold">📲 Εγκαταστάσεις:</span>
          <span>Σύνολο <b className="text-white" data-testid="stat-installs">{qrStats.installs ?? 0}</b></span>
          <span>Σήμερα <b className="text-white">{qrStats.installs_today ?? 0}</b></span>
          <span className="text-slate-500">•</span>
          <span className="text-sky-300 font-semibold">🔔 Ειδοποιήσεις:</span>
          <span><b className="text-white" data-testid="stat-notif-subs">{qrStats.notif_subscribers ?? 0}</b> συνδρομητές</span>
          <span className="text-slate-500">•</span>
          <span className="text-white font-semibold"> App Store πατήματα:</span>
          <span>Σύνολο <b className="text-white" data-testid="stat-appstore-clicks">{qrStats.app_store_clicks ?? 0}</b></span>
          <span>Σήμερα <b className="text-white">{qrStats.app_store_clicks_today ?? 0}</b></span>
          {(qrStats.app_store_by_device || []).map((d) => (
            <span key={`dev-${d.key}`} className="text-slate-400" data-testid={`stat-appstore-device-${d.key}`}>{d.key === 'ios' ? 'iPhone' : d.key === 'desktop' ? 'Υπολογιστής' : d.key} <b className="text-gold/90">{d.count}</b></span>
          ))}
          {(qrStats.app_store_by_country || []).slice(0, 6).map((c) => (
            <span key={`cty-${c.key}`} className="text-slate-400" data-testid={`stat-appstore-country-${c.key}`}>{c.key} <b className="text-gold/90">{c.count}</b></span>
          ))}
          <span className="text-slate-500">•</span>
          {(qrStats.by_lang || []).slice(0, 8).map((l) => (
            <span key={l.lang} className="text-slate-400">{String(l.lang).toUpperCase()} <b className="text-gold/90">{l.count}</b></span>
          ))}
          {role === 'admin' && (
            <button type="button" onClick={resetAnalytics} data-testid="reset-analytics-btn" className="ml-auto px-2 py-0.5 rounded-md border border-red-500/50 text-red-300 hover:bg-red-950/40 transition text-[11px] font-semibold">
              Καθαρισμός δοκιμαστικών
            </button>
          )}
        </div>
      )}
      <div className="p-4 border-b border-slate-700 bg-navy-200/50 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setTab('active')}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold transition ${tab === 'active' ? 'bg-gold text-navy' : 'bg-navy-200/60 text-slate-300 hover:text-white'}`}
            data-testid="chats-tab-active"
          >
            💬 Ενεργές ({activeCount})
            {totalUnread > 0 && <span className="ml-1.5 px-1.5 py-0.5 text-[10px] bg-red-500 text-white rounded-full font-bold">{totalUnread}</span>}
          </button>
          <button
            type="button"
            onClick={() => setTab('done')}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold transition ${tab === 'done' ? 'bg-emerald-500 text-white' : 'bg-navy-200/60 text-slate-300 hover:text-white'}`}
            data-testid="chats-tab-done"
          >
            ✅ Ολοκληρωμένες ({doneCount})
          </button>
          <button
            type="button"
            onClick={() => setTab('deleted')}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold transition ${tab === 'deleted' ? 'bg-red-500 text-white' : 'bg-navy-200/60 text-slate-300 hover:text-white'}`}
            data-testid="chats-tab-deleted"
          >
            🗑️ Σβησμένες ({deletedCount})
          </button>
        </div>
        <button type="button" onClick={fetchChats} className="text-xs text-slate-400 hover:text-gold transition" data-testid="visitor-chats-refresh">🔄 Ανανέωση</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr]" style={{ minHeight: 500 }}>
        {/* Session list */}
        <div className="border-r border-slate-700/50 max-h-[600px] overflow-y-auto">
          {authError ? (
            <div className="text-center py-10 px-4" data-testid="visitor-chats-auth-error">
              <p className="text-red-300 text-sm font-semibold mb-1">Η σύνδεση έληξε</p>
              <p className="text-slate-400 text-xs mb-4">Συνδεθείτε ξανά για να δείτε τις συνομιλίες.</p>
              <button
                type="button"
                data-testid="visitor-chats-relogin-btn"
                onClick={() => {
                  try { localStorage.removeItem('partnerToken'); localStorage.removeItem('jhouse_partner_user'); } catch (_e) {}
                  window.location.reload();
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gold text-navy text-sm font-semibold hover:bg-gold/90 transition"
              >
                Σύνδεση ξανά
              </button>
            </div>
          ) : visibleChats.length === 0 ? (
            <p className="text-slate-400 text-center py-10 text-sm px-4">{tab === 'deleted' ? 'Καμία σβησμένη συνομιλία.' : tab === 'done' ? 'Καμία ολοκληρωμένη συνομιλία.' : 'Καμία ενεργή συνομιλία.'}</p>
          ) : (
            visibleChats.map((chat) => {
              const closed = isClosed(chat);
              const owner = chat.assigned_agent;
              const ownedByOther = owner && owner !== me && role !== 'admin';
              return (
                <button
                  key={chat.session_id}
                  type="button"
                  onClick={() => openChat(chat.session_id)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-800/50 transition ${
                    activeSessionId === chat.session_id ? 'bg-gold/10 border-l-4 border-l-gold' : 'hover:bg-navy-200/50'
                  } ${closed ? 'bg-emerald-900/15 border-l-4 border-l-emerald-500' : ''}`}
                  data-testid={`visitor-chat-item-${chat.session_id}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-white text-sm font-semibold truncate flex items-center gap-1.5">
                      {closed && <span className="text-emerald-400">✓</span>}
                      {chat.name}
                    </span>
                    {!closed && chat.unread_admin > 0 && (
                      <span className="px-1.5 py-0.5 text-[10px] bg-red-500 text-white rounded-full font-bold flex-shrink-0">{chat.unread_admin}</span>
                    )}
                  </div>
                  {chat.phone && <p className="text-[10px] text-slate-500 mb-1">📞 {chat.phone}</p>}
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-[10px] text-slate-400">{formatTime(chat.last_visitor_message_at || chat.updated_at)}</p>
                    {chat.visitor_lang && (() => {
                      const b = langBadge(chat.visitor_lang);
                      return b ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-navy-200/70 border border-gold/30 text-[10px] text-gold/90 flex-shrink-0" data-testid="chat-lang-badge">
                          <span className="text-xs leading-none">{b[0]}</span>{b[1]}
                        </span>
                      ) : null;
                    })()}
                  </div>
                  {owner && (
                    <p className={`text-[10px] mt-1 flex items-center gap-1 ${ownedByOther ? 'text-amber-400' : 'text-emerald-400'}`} data-testid="chat-assigned-badge">
                      {ownedByOther ? '🔒' : '👤'} {owner === me ? 'Εσείς' : chat.assigned_agent_name}
                    </p>
                  )}
                  {chat.rating ? (
                    <p className="text-[10px] mt-1 text-amber-400" data-testid={`chat-rating-badge-${chat.session_id}`}>
                      {'★'.repeat(chat.rating)}<span className="text-slate-600">{'★'.repeat(5 - chat.rating)}</span> <span className="text-slate-500">{chat.rating}/5</span>
                    </p>
                  ) : null}
                </button>
              );
            })
          )}
        </div>

        {/* Active conversation */}
        <div className="flex flex-col max-h-[600px]">
          {!activeSessionId ? (
            <div className="flex-1 flex items-center justify-center text-slate-500 text-sm p-8 text-center">
              Επιλέξτε μια συνομιλία από τη λίστα αριστερά για να ξεκινήσετε.
            </div>
          ) : (
            <>
              <div className="p-3 border-b border-slate-700/50 flex items-center justify-between bg-navy-200/30 flex-wrap gap-2">
                <div className="min-w-0">
                  <p className="text-white text-sm font-semibold flex items-center gap-2">
                    {activeClosed && <span className="text-emerald-400">✓</span>}
                    {activeChat?.name}
                  </p>
                  {activeChat?.phone && <a href={`tel:${activeChat.phone}`} className="text-xs text-gold hover:underline">📞 {activeChat.phone}</a>}
                  {activeChat?.assigned_agent && (
                    <p className={`text-[11px] mt-0.5 ${isLockedByOther(activeChat) ? 'text-amber-400' : 'text-emerald-400'}`} data-testid="active-assigned-label">
                      {isLockedByOther(activeChat) ? '🔒 Αναλήφθηκε από: ' : '👤 Αναλήφθηκε από: '}
                      {activeChat.assigned_agent === me ? 'Εσάς' : activeChat.assigned_agent_name}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {!activeClosed && !activeDeleted && !activeChat?.assigned_agent && (
                    <button type="button" onClick={claimChat} className="text-xs text-navy font-semibold rounded px-3 py-1.5" style={{ background: 'linear-gradient(180deg,#d4b661,#a17f2f)' }} data-testid="chat-claim-btn">Ανάληψη</button>
                  )}
                  {!activeClosed && !activeDeleted && canReply(activeChat) && (
                    <button type="button" onClick={completeChat} className="text-xs text-white font-semibold rounded px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 transition" data-testid="chat-complete-btn">✅ Ολοκλήρωση</button>
                  )}
                  {!activeClosed && !activeDeleted && canReply(activeChat) && (
                    <button type="button" onClick={openCompleteWithCollab} className="text-xs text-navy font-semibold rounded px-3 py-1.5" style={{ background: 'linear-gradient(180deg,#d4b661,#a17f2f)' }} data-testid="chat-complete-collab-btn">📅 Ραντεβού</button>
                  )}
                  {activeClosed && !activeDeleted && canReply(activeChat) && (
                    <button type="button" onClick={reopenChat} className="text-xs text-gold border border-gold/50 rounded px-3 py-1.5 hover:bg-gold/10 transition" data-testid="chat-reopen-btn">↩ Επαναφορά</button>
                  )}
                  {!activeClosed && !activeDeleted && canReply(activeChat) && (
                    <button type="button" onClick={openTransfer} className="text-xs text-gold font-semibold rounded px-3 py-1.5 border border-gold/50 hover:bg-gold/10 transition" data-testid="chat-transfer-btn">🔁 Μεταβίβαση</button>
                  )}
                  {activeDeleted ? (
                    role === 'admin' ? (
                      <button type="button" onClick={() => permanentDeleteChat(activeSessionId)} className="text-xs text-white font-semibold rounded px-3 py-1.5 bg-red-600 hover:bg-red-500 transition" data-testid="visitor-chat-permanent-delete">🗑️ Μόνιμη διαγραφή</button>
                    ) : (
                      <span className="text-[10px] text-slate-500 max-w-[180px] text-right" data-testid="visitor-chat-deleted-note">Σβησμένη — μόνο ο διαχειριστής μπορεί να τη διαγράψει μόνιμα</span>
                    )
                  ) : (
                    <button type="button" onClick={() => deleteChat(activeSessionId)} className="text-xs text-red-400 hover:text-red-300 transition px-2 py-1" data-testid="visitor-chat-delete" title="Μετακίνηση στις Σβησμένες">🗑️</button>
                  )}
                </div>
              </div>

              {activeClosed && (
                <div className="px-4 py-2 bg-emerald-900/25 border-b border-emerald-700/40 text-emerald-300 text-xs flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="chat-closed-banner">
                  <span>✅ Η συνομιλία ολοκληρώθηκε{activeChat?.completed_by ? ` από ${activeChat.completed_by}` : ''}.</span>
                  {activeChat?.rating ? (
                    <span className="inline-flex items-center gap-1 text-amber-300" data-testid="chat-rating-value">
                      Βαθμολογία: <span className="tracking-tight">{'★'.repeat(activeChat.rating)}<span className="text-slate-600">{'★'.repeat(5 - activeChat.rating)}</span></span>
                      <span className="text-slate-400">({activeChat.rating}/5)</span>
                    </span>
                  ) : (
                    <span className="text-slate-500" data-testid="chat-rating-none">Χωρίς βαθμολογία ακόμη</span>
                  )}
                  {activeChat?.rating_feedback ? (
                    <span className="w-full text-slate-300 italic" data-testid="chat-rating-feedback">💬 «{activeChat.rating_feedback}»</span>
                  ) : null}
                </div>
              )}

              {activeDeleted && (
                <div className="px-4 py-2 bg-red-900/25 border-b border-red-700/40 text-red-300 text-xs flex items-center gap-2" data-testid="chat-deleted-banner">
                  🗑️ Σβησμένη συνομιλία{activeChat?.deleted_by ? ` (από ${activeChat.deleted_by})` : ''} — διατηρείται για έλεγχο.
                </div>
              )}

              <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-navy/30">
                {loading ? (
                  <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-6 w-6 border-2 border-gold border-t-transparent" /></div>
                ) : messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] px-3.5 py-2 rounded-2xl text-sm leading-relaxed ${
                      m.sender === 'admin' ? 'bg-gold text-navy rounded-br-sm' : 'bg-navy-100 border border-slate-700/60 text-white rounded-bl-sm'
                    }`}>
                      {m.sender === 'admin' && m.sender_name && (
                        <div className="text-[9px] font-bold uppercase tracking-wider text-navy/60 mb-0.5">{m.sender_name}</div>
                      )}
                      {(m.msg_type === 'voice' || m.audio_url) ? (
                        <div className="flex items-center gap-2" data-testid="agent-voice-msg">
                          <span className="text-base">🎤</span>
                          <audio controls preload="none" src={`${fileBase}${m.audio_url}`} className="h-9 max-w-[190px]" />
                        </div>
                      ) : m.text}
                      {m.sender !== 'admin' && m.text_el && m.text_el.trim() && m.text_el.trim() !== (m.text || '').trim() && (
                        <div className="mt-1.5 pt-1.5 border-t border-slate-600/50 text-gold/90 text-[13px] leading-relaxed" data-testid="visitor-msg-translation">
                          <span className="inline-block text-[8px] font-bold uppercase tracking-wider text-gold/60 mr-1.5 align-middle">GR</span>{m.text_el}
                        </div>
                      )}
                      {m.sender === 'admin' && m.text_translated && m.text_translated.trim() && m.text_translated.trim() !== (m.text || '').trim() && (
                        <div className="mt-1.5 pt-1.5 border-t border-navy/25 text-navy/70 text-[13px] leading-relaxed" data-testid="admin-reply-translation">
                          <span className="inline-block text-[10px] font-bold text-navy/50 mr-1.5 align-middle">↳</span>{m.text_translated}
                        </div>
                      )}
                      <div className={`text-[9px] mt-1 opacity-60 ${m.sender === 'admin' ? 'text-navy' : 'text-slate-400'}`}>{formatTime(m.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>

              {lockMsg && <div className="px-4 py-2 bg-amber-900/30 text-amber-300 text-xs" data-testid="chat-lock-msg">{lockMsg}</div>}

              {activeDeleted ? (
                <div className="p-3 border-t border-slate-700/50 text-center text-slate-400 text-xs" data-testid="chat-deleted-notice">
                  Σβησμένη συνομιλία (μόνο για ανάγνωση).
                </div>
              ) : lockedActive ? (
                <div className="p-3 border-t border-slate-700/50 text-center text-amber-400 text-xs bg-amber-900/10" data-testid="chat-readonly-notice">
                  🔒 Η συνομιλία εξυπηρετείται από {activeChat.assigned_agent_name}. Δεν μπορείτε να απαντήσετε.
                </div>
              ) : activeClosed ? (
                <div className="p-3 border-t border-slate-700/50 text-center text-slate-400 text-xs" data-testid="chat-closed-notice">
                  Η συνομιλία είναι ολοκληρωμένη. Πατήστε «Επαναφορά» για να απαντήσετε ξανά.
                </div>
              ) : (
                <div className="border-t border-slate-700/50">
                  <div className="px-3 pt-2.5 flex flex-wrap gap-1.5" data-testid="quick-questions">
                    {quickReplies.map((q, i) => (
                      <button
                        key={q.id || i}
                        type="button"
                        onClick={() => sendQuick(q.text)}
                        className="text-[11px] px-2.5 py-1 rounded-full bg-navy-200/70 border border-gold/30 text-gold/90 hover:bg-gold/15 hover:border-gold/60 transition"
                        data-testid={`quick-q-${i}`}
                        title="Αποστολή αυτόματης ερώτησης"
                      >
                        {q.text}
                      </button>
                    ))}
                  </div>
                  <div className="p-3 flex items-center gap-2">
                    {recording ? (
                      <div className="flex-1 flex items-center gap-2" data-testid="agent-recording-bar">
                        <button type="button" onClick={() => stopRecording(true)} className="w-9 h-9 rounded-full bg-navy-100 border border-slate-600 text-slate-300 hover:text-white flex items-center justify-center transition" data-testid="agent-rec-cancel" aria-label="Cancel">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" /></svg>
                        </button>
                        <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-full bg-red-950/40 border border-red-500/50">
                          <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
                          <span className="text-red-200 text-sm font-mono">{String(Math.floor(recSecs / 60))}:{String(recSecs % 60).padStart(2, '0')}</span>
                          <span className="text-red-300/70 text-xs ml-auto">● REC</span>
                        </div>
                        <button type="button" onClick={() => stopRecording(false)} className="w-10 h-10 rounded-full bg-gold hover:bg-gold/90 text-navy flex items-center justify-center transition" data-testid="agent-rec-send" aria-label="Send voice">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" /></svg>
                        </button>
                      </div>
                    ) : (
                      <>
                        <input
                          type="text"
                          value={reply}
                          onChange={onReplyChange}
                          onKeyDown={(e) => { if (e.key === 'Enter') sendReply(); }}
                          placeholder="Απάντηση..."
                          className="flex-1 px-4 py-2.5 rounded-full bg-navy-100 border border-slate-700 focus:border-gold text-white text-sm outline-none transition"
                          data-testid="visitor-chat-reply-input"
                        />
                        {reply.trim() ? (
                          <button
                            type="button"
                            onClick={sendReply}
                            className="w-10 h-10 rounded-full bg-gold hover:bg-gold/90 text-navy flex items-center justify-center transition"
                            data-testid="visitor-chat-reply-send"
                            aria-label="Send"
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" /></svg>
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={startRecording}
                            className="w-10 h-10 rounded-full bg-gold hover:bg-gold/90 text-navy flex items-center justify-center transition"
                            data-testid="visitor-chat-reply-mic"
                            aria-label="Record voice"
                            title="Φωνητικό μήνυμα"
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 14a3 3 0 003-3V5a3 3 0 00-6 0v6a3 3 0 003 3z" /><path d="M19 11a7 7 0 01-14 0H3a9 9 0 008 8.94V23h2v-3.06A9 9 0 0021 11h-2z" /></svg>
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {completeModal && cForm && (
        <div className="fixed inset-0 z-[10001] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={() => setCompleteModal(false)} data-testid="complete-collab-modal">
          <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-lg my-8 rounded-2xl p-5 md:p-6" style={{ background: 'rgba(12,22,40,0.97)', border: '1px solid rgba(201,168,76,0.4)' }}>
            <button onClick={() => setCompleteModal(false)} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-navy border border-gold/50 text-gold flex items-center justify-center hover:bg-gold/10" data-testid="complete-collab-close" aria-label="Close">✕</button>
            <h3 className="text-gold text-lg font-bold mb-1" style={{ fontFamily: 'Georgia, serif' }}>Κλείσιμο ραντεβού με συνεργάτη</h3>
            <p className="text-slate-400 text-xs mb-4">Επιλέξτε συνεργάτη, ημερομηνία & ώρα ραντεβού και συμπληρώστε τα στοιχεία του ασθενή. Θα καταχωρηθεί επίσκεψη και θα σταλούν τα στοιχεία του ραντεβού στον ασθενή για επιβεβαίωση. Η συνομιλία παραμένει ανοιχτή.</p>

            <label className="block text-slate-300 text-xs mb-1">Συνεργάτης</label>
            <select value={cForm.collaborator_id} onChange={(e) => setCForm({ ...cForm, collaborator_id: e.target.value })} className="w-full mb-3 px-3 py-2.5 rounded-lg bg-navy-200/60 border border-slate-700 text-white text-sm focus:border-gold outline-none" data-testid="complete-collab-select">
              <option value="">— Επιλέξτε —</option>
              {collabs.map((c) => (
                <option key={c.id} value={c.id}>{c.specialty} — {c.full_name}{c.city ? ` (${c.city})` : ''}</option>
              ))}
            </select>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
              <input value={cForm.first_name} onChange={(e) => setCForm({ ...cForm, first_name: e.target.value })} placeholder="Όνομα" className="px-3 py-2 rounded-lg bg-navy-200/60 border border-slate-700 text-white text-sm focus:border-gold outline-none" data-testid="complete-first-name" />
              <input value={cForm.last_name} onChange={(e) => setCForm({ ...cForm, last_name: e.target.value })} placeholder="Επίθετο" className="px-3 py-2 rounded-lg bg-navy-200/60 border border-slate-700 text-white text-sm focus:border-gold outline-none" data-testid="complete-last-name" />
              <input value={cForm.phone} onChange={(e) => setCForm({ ...cForm, phone: e.target.value })} placeholder="Τηλέφωνο" className="px-3 py-2 rounded-lg bg-navy-200/60 border border-slate-700 text-white text-sm focus:border-gold outline-none" data-testid="complete-phone" />
              <input type="date" value={cForm.visit_date} onChange={(e) => setCForm({ ...cForm, visit_date: e.target.value })} className="px-3 py-2 rounded-lg bg-navy-200/60 border border-slate-700 text-white text-sm focus:border-gold outline-none" data-testid="complete-date" />
              <input type="text" inputMode="numeric" placeholder="Ώρα (π.χ. 14:30)" pattern="[0-9]{1,2}:[0-9]{2}" value={cForm.visit_time} onChange={(e) => setCForm({ ...cForm, visit_time: e.target.value })} className="px-3 py-2 rounded-lg bg-navy-200/60 border border-slate-700 text-white text-sm focus:border-gold outline-none" data-testid="complete-time" />
            </div>
            <textarea value={cForm.comments} onChange={(e) => setCForm({ ...cForm, comments: e.target.value })} placeholder="Σχόλια / περιγραφή περιστατικού" rows={2} className="w-full px-3 py-2 rounded-lg bg-navy-200/60 border border-slate-700 text-white text-sm focus:border-gold outline-none resize-none mb-3" data-testid="complete-comments" />
            {cErr && <p className="text-red-400 text-sm mb-2" data-testid="complete-collab-error">{cErr}</p>}
            <div className="flex gap-2">
              <button type="button" onClick={submitCompleteWithCollab} disabled={saving} className="flex-1 py-2.5 rounded-lg font-semibold text-navy text-sm disabled:opacity-60" style={{ background: 'linear-gradient(180deg,#d4b661,#a17f2f)' }} data-testid="complete-collab-submit">{saving ? '...' : '📅 Κλείσιμο ραντεβού & αποστολή στοιχείων'}</button>
              <button type="button" onClick={() => setCompleteModal(false)} className="px-4 py-2.5 rounded-lg text-slate-300 text-sm border border-slate-600">Άκυρο</button>
            </div>
          </div>
        </div>
      )}

      {transferOpen && (
        <div className="fixed inset-0 z-[10001] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={() => setTransferOpen(false)} data-testid="transfer-modal">
          <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-md my-8 rounded-2xl p-5 md:p-6" style={{ background: 'rgba(12,22,40,0.97)', border: '1px solid rgba(201,168,76,0.4)' }}>
            <button onClick={() => setTransferOpen(false)} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-navy border border-gold/50 text-gold flex items-center justify-center hover:bg-gold/10" data-testid="transfer-modal-close" aria-label="Close">✕</button>
            <h3 className="text-gold text-lg font-bold mb-1" style={{ fontFamily: 'Georgia, serif' }}>Μεταβίβαση συνομιλίας</h3>
            <p className="text-slate-400 text-xs mb-4">Επιλέξτε συνδεδεμένο αντιπρόσωπο ή τον διαχειριστή. Θα λάβει ειδοποίηση και θα αποδεχτεί ή θα απορρίψει.</p>
            {onlineStaff.length === 0 ? (
              <p className="text-slate-400 text-sm py-4 text-center" data-testid="transfer-no-online">Κανένας άλλος αντιπρόσωπος δεν είναι συνδεδεμένος αυτή τη στιγμή.</p>
            ) : (
              <div className="space-y-2 mb-4 max-h-64 overflow-y-auto" data-testid="transfer-online-list">
                {onlineStaff.map((s) => (
                  <label key={s.username} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition ${transferTarget === s.username ? 'border-gold bg-gold/10' : 'border-slate-700 hover:border-gold/50'}`} data-testid={`transfer-option-${s.username}`}>
                    <input type="radio" name="transfer-target" value={s.username} checked={transferTarget === s.username} onChange={() => setTransferTarget(s.username)} className="accent-amber-500" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 flex-shrink-0" title="Online" />
                    <span className="text-white text-sm font-medium flex-1">{s.name}</span>
                    <span className="text-[10px] uppercase tracking-wide text-gold/80">{s.role === 'admin' ? 'Διαχειριστής' : 'Αντιπρόσωπος'}</span>
                  </label>
                ))}
              </div>
            )}
            <button type="button" onClick={submitTransfer} disabled={!transferTarget} className="w-full py-2.5 rounded-lg font-semibold text-navy text-sm disabled:opacity-50" style={{ background: 'linear-gradient(180deg,#d4b661,#a17f2f)' }} data-testid="transfer-submit">🔁 Αποστολή αιτήματος μεταβίβασης</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitorChatsPanel;
